import type { LampTone } from '../../ui/StatusLamp.tsx';
import type { TargetProbeLegRecord, TargetProbeTripReason } from '@nadoutong/contracts';
import type { I18nKey } from '../../i18n/index.ts';

export type LegLamp = 'ok' | 'shadow' | 'tripped' | 'manual' | 'unprobed' | 'immune' | 'failing';

export function minutesBefore(iso: string, nowMs: number): number | null {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.floor((nowMs - parsed) / 60_000));
}

type AgoTranslate = (key: I18nKey, params?: Readonly<Record<string, string>>) => string;

/**
 * 「刚刚」 under a minute, 「N 分钟前」 below an hour, 「N 小时前」 below 48 hours, else 「N 天前」:
 * a raw 「2815 分钟前」 is unreadable. Empty when `iso` is absent or bad.
 */
export function legAgoText(iso: string | null, nowMs: number, t: AgoTranslate): string {
  if (iso === null) return '';
  const minutes = minutesBefore(iso, nowMs);
  if (minutes === null) return '';
  if (minutes < 1) return t('tenant_routing.ago.just_now');
  if (minutes < 60) return t('guaranteed.leg.minutes_ago', { minutes: String(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return t('guaranteed.legs.ago.hours', { hours: String(hours) });
  return t('guaranteed.legs.ago.days', { days: String(Math.floor(hours / 24)) });
}

export function minutesUntil(iso: string | null, nowMs: number): number | null {
  if (iso === null) return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.ceil((parsed - nowMs) / 60_000));
}

/**
 * When the scheduler will next probe this region slice. The gateway writes
 * `next_probe_at` only while a leg is tripped (the backoff schedule, see
 * `state-machine.ts`); an untripped leg is due at `last_probe_at + interval`
 * (`schedule.ts` `isProbeDue`) with the column left NULL. Null when neither
 * is known (never probed, or the SKU interval is unknown).
 */
export function legNextProbeMs(leg: TargetProbeLegRecord, intervalMinutes: number | null): number | null {
  const explicit = leg.next_probe_at === null ? Number.NaN : Date.parse(leg.next_probe_at);
  if (Number.isFinite(explicit)) return explicit;
  if (intervalMinutes === null || intervalMinutes <= 0 || leg.last_probe_at === null) return null;
  const last = Date.parse(leg.last_probe_at);
  return Number.isFinite(last) ? last + intervalMinutes * 60_000 : null;
}

export function legMatchesQuery(leg: TargetProbeLegRecord, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  return `${leg.sku_name}\n${leg.upstream_name}\n${leg.physical_model}\n${leg.upstream_channel_id}`
    .toLowerCase()
    .includes(q);
}

/** Whole minutes still covered. Null once `immune_until` is absent or past. */
export function immunityMinutesRemaining(iso: string | null, nowMs: number): number | null {
  if (iso === null) return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed) || parsed <= nowMs) return null;
  return Math.max(1, Math.ceil((parsed - nowMs) / 60_000));
}

/**
 * How the SKU that owns a leg is configured. `probesOn` = probe mode is not
 * off (the row has a next-probe time that means something); `armed` = at
 * least one mechanism is in act mode, so a `tripped` row actually demotes
 * the leg. A tripped row on an unarmed SKU is a leftover, not a live removal.
 */
export interface LegSkuArming {
  readonly probesOn: boolean;
  /**
   * Probes in act: they can release a chain / Jev trip after clean rounds
   * (router#2664). Absent = unknown (say nothing).
   */
  readonly probeAct?: boolean;
  readonly armed: boolean;
  /** SKU probe criteria; null when the SKU row is unknown. */
  readonly tripFailedRounds: number | null;
  readonly recoverOkRounds: number | null;
}

export const LEG_SKU_UNKNOWN: LegSkuArming = {
  probesOn: true,
  armed: true,
  tripFailedRounds: null,
  recoverOkRounds: null,
};

/** Anchor member for probe-now / reset: an enabled one first, else the first. */
export function legAnchorMember(leg: TargetProbeLegRecord): TargetProbeLegRecord['members'][number] | undefined {
  return leg.members.find((member) => member.enabled) ?? leg.members[0];
}

export function legIsManual(leg: TargetProbeLegRecord): boolean {
  return leg.members.length > 0 && leg.members.every((member) => !member.enabled);
}

export function legLamp(leg: TargetProbeLegRecord, nowMs: number): LegLamp {
  if (legIsManual(leg)) return 'manual';
  if (leg.tripped) return 'tripped';
  if (leg.shadow_trip_reason !== null) return 'shadow';
  if (immunityMinutesRemaining(leg.immune_until, nowMs) !== null) return 'immune';
  if (leg.last_probe_at === null) return 'unprobed';
  if (legLatestProbeFailed(leg)) return 'failing';
  return 'ok';
}

/**
 * Latest probe result was not a success, but the leg is below the trip line
 * (one failed shot does not trip by default). Must not read as healthy.
 */
export function legLatestProbeFailed(leg: TargetProbeLegRecord): boolean {
  const code = leg.last_status_code;
  if (code !== null) return code < 200 || code > 299;
  if (leg.last_error_excerpt === null) return false;
  if (leg.last_ok_at === null || leg.last_probe_at === null) return true;
  return Date.parse(leg.last_ok_at) < Date.parse(leg.last_probe_at);
}

export function legLampTone(lamp: LegLamp): LampTone {
  switch (lamp) {
    case 'ok':
      return 'ok';
    case 'shadow':
    case 'failing':
      return 'warn';
    case 'tripped':
      return 'bad';
    case 'immune':
      return 'signal';
    case 'manual':
    case 'unprobed':
      return 'off';
  }
}

export function tripReasonKey(reason: TargetProbeTripReason | null): I18nKey {
  switch (reason) {
    case 'silent':
      return 'guaranteed.leg.trigger.silent';
    case 'chain':
      return 'tenant_routing.probe.reason.chain';
    case 'jev':
      return 'tenant_routing.probe.reason.jev';
    case 'quality':
      return 'tenant_routing.probe.reason.quality';
    case 'probe':
    case null:
      return 'tenant_routing.probe.reason.probe';
  }
}

export const IMMUNITY_CHOICES = ['0', '5', '10', '30', '60'] as const;
export type ImmunityChoice = (typeof IMMUNITY_CHOICES)[number];

export function isImmunityChoice(value: string): value is ImmunityChoice {
  return IMMUNITY_CHOICES.some((choice) => choice === value);
}

export function immunityMinutes(choice: ImmunityChoice): number {
  switch (choice) {
    case '0':
      return 0;
    case '5':
      return 5;
    case '10':
      return 10;
    case '30':
      return 30;
    case '60':
      return 60;
  }
}
